import { useEffect, useState } from "react";
import { Container, Card, Badge, Spinner, Button, Row, Col, Image, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

export default function Registry() {
  const navigate = useNavigate();
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [yearInputValue, setYearInputValue] = useState(new Date().getFullYear().toString()); // Local state for year input
  const [loadingFacts, setLoadingFacts] = useState(false);
  const [dateFacts, setDateFacts] = useState(null); // {title, description} or null

  // Decode token to get current user's ID
  const token = localStorage.getItem("token");
  let currentUserId = null;
  if (token) {
    try {
      const decoded = jwtDecode(token);
      currentUserId = decoded.id; // <-- use id from JWT
    } catch (err) {
      // Invalid token - will be handled by error state
    }
  }

  const generateCertificate = async (day) => {
    const token = localStorage.getItem("token");

    try {
      const res = await fetch(`/api/pdf/${day}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        // Try to get error message from response
        let errorMessage = "Failed to generate PDF";
        try {
          const errorData = await res.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // If response is not JSON, use default message
        }
        throw new Error(errorMessage);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");

      // Optional: revoke URL after some time
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      alert(err.message || "Could not generate certificate. Please try again.");
    }
  };

  // Fetch registry data for the current month
  useEffect(() => {
    const fetchDays = async () => {
      try {
        setLoading(true);
        // Fetch days for the currently displayed month
        const res = await fetch(`/api/days/registry?month=${currentMonth}&year=${currentYear}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.message || "Failed to fetch registry");
        }
        const data = await res.json();
        // Ensure days is always an array, even if API returns null/undefined
        setDays(Array.isArray(data) ? data : []);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchDays();
    } else {
      setError("Not authenticated");
      setLoading(false);
    }
  }, [token, currentMonth, currentYear]);

  // Sync yearInputValue when currentYear changes from other sources (navigation buttons, etc.)
  useEffect(() => {
    setYearInputValue(currentYear.toString());
  }, [currentYear]);

  // Helper function to get day object by date string
  const getDayByDate = (dateString) => {
    if (!Array.isArray(days) || days.length === 0) {
      return null;
    }
    return days.find((day) => day && day.date === dateString) || null;
  };

  // Calendar helper functions
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const formatDate = (year, month, day) => {
    const monthStr = String(month + 1).padStart(2, "0");
    const dayStr = String(day).padStart(2, "0");
    return `${year}-${monthStr}-${dayStr}`;
  };

  const fetchDateFacts = async (dateString) => {
    const token = localStorage.getItem("token");
    setLoadingFacts(true);
    setDateFacts(null);
    try {
      // Check if date is today or in the future - don't generate description for these
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const inputDate = new Date(dateString + "T00:00:00");
      inputDate.setHours(0, 0, 0, 0);

      if (inputDate >= today) {
        // For today and future dates, don't generate any description
        setDateFacts(null);
        setLoadingFacts(false);
        return;
      }

      // Only make API call for past dates
      const res = await fetch("/api/openai/date-facts", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ date: dateString }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        const facts = {
          title: data.title || "",
          description: data.description || "",
        };
        setDateFacts(facts);
      } else {
        // For past dates that fail, show error message
        setDateFacts({
          title: "Unable to Load Facts",
          description: data.message || "Failed to fetch historical facts for this date. Please try again later.",
        });
        console.error("Failed to fetch date facts:", data.message);
      }
    } catch (err) {
      console.error("Error fetching date facts:", err);
      setDateFacts({
        title: "Error",
        description: "An error occurred while fetching historical facts. Please try again later.",
      });
    } finally {
      setLoadingFacts(false);
    }
  };

  const handleDayClick = (dateString) => {
    const day = getDayByDate(dateString);
    // Allow clicking on any day - purchased or unpurchased
    if (day) {
      setSelectedDay(day);
      setDateFacts(null); // Clear facts when selecting a purchased day
      setLoadingFacts(false); // Clear loading state
    } else {
      // For unpurchased days, check if it's a past date before fetching facts
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const inputDate = new Date(dateString + "T00:00:00");
      inputDate.setHours(0, 0, 0, 0);

      setSelectedDay({
        date: dateString,
        isUnpurchased: true,
        isFutureOrToday: inputDate >= today, // Mark if it's today or future
      });

      // Only fetch facts for past dates
      if (inputDate < today) {
        fetchDateFacts(dateString);
      } else {
        // For today and future dates, don't generate description
        setDateFacts(null);
        setLoadingFacts(false);
      }
    }
  };

  const navigateMonth = (direction) => {
    // Calculate new month and year
    let newMonth, newYear;
    
    if (direction === "prev") {
      if (currentMonth === 0) {
        // January -> December of previous year
        newMonth = 11;
        newYear = currentYear - 1;
      } else {
        // Just go back one month
        newMonth = currentMonth - 1;
        newYear = currentYear;
      }
    } else {
      // direction === "next"
      if (currentMonth === 11) {
        // December -> January of next year
        newMonth = 0;
        newYear = currentYear + 1;
      } else {
        // Just go forward one month
        newMonth = currentMonth + 1;
        newYear = currentYear;
      }
    }
    
    // Update both month and year
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
    
    // Update year input value if year changed
    if (newYear !== currentYear) {
      setYearInputValue(newYear.toString());
    }
  };

  const jumpToToday = () => {
    const today = new Date();
    const todayYear = today.getFullYear();
    setCurrentMonth(today.getMonth());
    setCurrentYear(todayYear);
    setYearInputValue(todayYear.toString());
  };

  const handleYearChange = (e) => {
    const value = e.target.value;
    // Update local input value only (doesn't trigger fetch)
    setYearInputValue(value);
  };

  const handleYearBlur = (e) => {
    const year = parseInt(e.target.value);
    if (isNaN(year) || year < 1900 || year > 2100) {
      // Reset to current year if invalid
      const today = new Date();
      const validYear = today.getFullYear();
      setYearInputValue(validYear.toString());
      setCurrentYear(validYear);
    } else {
      // Only update currentYear (which triggers fetch) when valid and on blur
      setCurrentYear(year);
      setYearInputValue(year.toString());
    }
  };

  const handleMonthChange = (e) => {
    const month = parseInt(e.target.value);
    setCurrentMonth(month);
  };


  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const calendarDays = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      calendarDays.push(null);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = formatDate(currentYear, currentMonth, day);
      const dayData = getDayByDate(dateString);
      calendarDays.push({ day, dateString, dayData });
    }

    const currentDate = new Date();
    const currentYearNum = currentDate.getFullYear();
    const years = [];
    for (let y = currentYearNum - 10; y <= currentYearNum + 50; y++) {
      years.push(y);
    }

    return (
      <div className="calendar-container">
        <div className="calendar-navigator">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <Button className="brutal-btn brutal-btn-secondary" size="sm" onClick={() => navigateMonth("prev")}>
              ‹ PREV
            </Button>
            <Button className="brutal-btn brutal-btn-primary" size="sm" onClick={jumpToToday}>
              TODAY
            </Button>
            <Button className="brutal-btn brutal-btn-secondary" size="sm" onClick={() => navigateMonth("next")}>
              NEXT ›
            </Button>
          </div>
          
          <div className="d-flex gap-2 align-items-center justify-content-center flex-wrap mb-3">
            <Form.Select
              value={currentMonth}
              onChange={handleMonthChange}
              className="brutal-form-control"
              style={{ width: "auto", minWidth: "140px" }}
              size="sm"
            >
              {monthNames.map((month, index) => (
                <option key={index} value={index}>
                  {month.toUpperCase()}
                </option>
              ))}
            </Form.Select>
            
             <div className="d-flex align-items-center gap-1">
               <Form.Label className="brutal-subheading mb-0" style={{ fontSize: "12px", whiteSpace: "nowrap" }}>YEAR:</Form.Label>
               <Form.Control
                 type="number"
                 value={yearInputValue}
                 onChange={handleYearChange}
                 onBlur={handleYearBlur}
                 onKeyDown={(e) => {
                   // Allow Enter key to trigger blur validation
                   if (e.key === "Enter") {
                     e.target.blur();
                   }
                 }}
                 min="1900"
                 max="2100"
                 className="brutal-form-control"
                 style={{ width: "100px" }}
                 size="sm"
                 placeholder="Year"
               />
             </div>
          </div>
          
          <div className="text-center">
            <h5 className="brutal-heading mb-0" style={{ fontSize: "24px" }}>{monthNames[currentMonth].toUpperCase()} {currentYear}</h5>
          </div>
        </div>
        <div className="calendar-grid">
          {dayNames.map((dayName) => (
            <div key={dayName} className="calendar-header-cell">
              {dayName}
            </div>
          ))}
          {calendarDays.map((cell, index) => {
            if (!cell) {
              return <div key={`empty-${index}`} className="calendar-cell empty"></div>;
            }
            const { day, dateString, dayData } = cell;
            const isSelected = selectedDay && selectedDay.date === dateString;
            const isToday = dateString === formatDate(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());

            const isUnpurchased = !dayData;
            return (
              <div
                key={dateString}
                className={`calendar-cell ${dayData ? "has-day" : "unpurchased"} ${isSelected ? "selected" : ""} ${isToday ? "today" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDayClick(dateString);
                }}
                onMouseDown={(e) => e.preventDefault()}
                style={{
                  cursor: "pointer",
                  backgroundColor: isSelected 
                    ? "#0d6efd" 
                    : dayData 
                      ? "#e7f3ff" 
                      : "#f0f8f0",
                  color: isSelected ? "white" : isUnpurchased ? "#28a745" : "inherit",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                }}
              >
                {day}
                {dayData && (
                  <div className="calendar-day-indicator">●</div>
                )}
                {isUnpurchased && (
                  <div className="calendar-day-indicator" style={{ color: "#28a745" }}>●</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };


  return (
    <Container className="mt-5 px-3 px-md-4">
      <Card className="brutal-card">
        <Card.Body className="p-4">
          <Card.Title className="brutal-heading mb-4" style={{ fontSize: "36px" }}>REGISTRY</Card.Title>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : error ? (
            <p className="text-danger text-center py-4">{error}</p>
          ) : (
            <Row>
              <Col xs={12} md={selectedDay ? 6 : 12}>
                <div className="calendar-wrapper">
                  {renderCalendar()}
                </div>
                <style>{`
                  .calendar-container {
                    max-width: 100%;
                  }
                  .calendar-navigator {
                    background-color: #FFFFFF;
                    padding: 20px;
                    border: 5px solid #000000;
                    box-shadow: 8px 8px 0px #000000;
                    margin-bottom: 20px;
                  }
                  .calendar-grid {
                    display: grid;
                    grid-template-columns: repeat(7, 1fr);
                    gap: 4px;
                    margin-top: 10px;
                    border: 4px solid #000000;
                    padding: 4px;
                    background: #000000;
                  }
                  .calendar-header-cell {
                    text-align: center;
                    font-weight: 900;
                    padding: 12px 8px;
                    background-color: #000000;
                    color: #FFFFFF;
                    font-size: 14px;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    border: 2px solid #000000;
                  }
                  .calendar-cell {
                    aspect-ratio: 1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    border: 3px solid #000000;
                    padding: 4px;
                    position: relative;
                    transition: all 0.1s;
                    pointer-events: auto;
                    z-index: 1;
                    background: #FFFFFF;
                    font-weight: 700;
                    min-height: 44px;
                    touch-action: manipulation;
                  }
                  @media (max-width: 768px) {
                    .calendar-navigator {
                      padding: 12px;
                      margin-bottom: 15px;
                    }
                    .calendar-navigator .btn {
                      font-size: 11px;
                      padding: 8px 12px;
                      min-height: 44px;
                      min-width: 80px;
                      flex: 1;
                    }
                    .calendar-navigator .d-flex {
                      flex-wrap: wrap;
                      gap: 8px;
                    }
                    .calendar-navigator .d-flex.justify-content-between {
                      gap: 8px;
                    }
                    .calendar-navigator .d-flex.justify-content-between .btn {
                      flex: 1;
                      min-width: 0;
                    }
                    .calendar-navigator .form-select,
                    .calendar-navigator .form-control {
                      font-size: 14px;
                      min-height: 44px;
                      padding: 10px 12px;
                    }
                    .calendar-navigator .d-flex.gap-1 .btn {
                      min-width: 44px;
                      padding: 8px 10px;
                      font-size: 10px;
                    }
                    .calendar-navigator h5 {
                      font-size: 18px !important;
                      margin-bottom: 0 !important;
                    }
                    .calendar-header-cell {
                      padding: 8px 2px;
                      font-size: 10px;
                      letter-spacing: 0;
                    }
                    .calendar-cell {
                      font-size: 12px;
                      min-height: 44px;
                      padding: 2px;
                      border-width: 2px;
                      cursor: pointer;
                      -webkit-tap-highlight-color: transparent;
                    }
                    .calendar-cell:active {
                      transform: scale(0.95);
                    }
                    .calendar-grid {
                      gap: 2px;
                      padding: 2px;
                      border-width: 3px;
                    }
                    .calendar-day-indicator {
                      font-size: 0.6rem;
                      margin-top: 1px;
                    }
                    .calendar-wrapper {
                      overflow-x: auto;
                      -webkit-overflow-scrolling: touch;
                    }
                  }
                  
                  @media (max-width: 575px) {
                    .calendar-header-cell {
                      font-size: 9px;
                      padding: 6px 1px;
                    }
                    .calendar-cell {
                      font-size: 11px;
                      min-height: 40px;
                    }
                    .calendar-navigator {
                      padding: 10px;
                    }
                    .calendar-navigator .btn {
                      font-size: 10px;
                      padding: 6px 10px;
                    }
                    .calendar-navigator .d-flex.gap-1 {
                      flex-wrap: wrap;
                      width: 100%;
                    }
                    .calendar-navigator .d-flex.gap-1 .btn {
                      flex: 1;
                      min-width: 60px;
                    }
                    .calendar-navigator .form-select,
                    .calendar-navigator .form-control {
                      width: 100% !important;
                      margin-bottom: 8px;
                    }
                    .calendar-navigator h5 {
                      font-size: 16px !important;
                    }
                  }
                  .calendar-cell.empty {
                    pointer-events: none;
                    border: none;
                    background-color: transparent;
                  }
                  .calendar-cell.has-day:hover {
                    background-color: #0000FF !important;
                    color: #FFFFFF !important;
                    transform: translate(2px, 2px);
                    box-shadow: 4px 4px 0px #000000;
                  }
                  .calendar-cell.unpurchased:hover {
                    background-color: #00FF00 !important;
                    color: #000000 !important;
                    transform: translate(2px, 2px);
                    box-shadow: 4px 4px 0px #000000;
                  }
                  .calendar-cell.selected {
                    font-weight: 900;
                    border: 4px solid #000000;
                    box-shadow: 6px 6px 0px #000000;
                    background: #FFFF00 !important;
                    color: #000000 !important;
                  }
                  .calendar-cell.today {
                    border: 4px solid #FF0000;
                    background: #FFFFFF;
                  }
                  .calendar-day-indicator {
                    font-size: 0.7rem;
                    margin-top: 2px;
                    font-weight: 900;
                  }
                `}</style>
              </Col>
              {selectedDay && (
                <Col xs={12} md={6} className="mt-4 mt-md-0">
                  <Card className="h-100 shadow-sm">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <Card.Title className="mb-0">{selectedDay.date}</Card.Title>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setSelectedDay(null)}
                        >
                          ✕
                        </Button>
                      </div>

                      {selectedDay.isUnpurchased ? (
                        <>
                          <div className="text-center py-4">
                            <h5 className="text-muted mb-3">📅 Available Day</h5>
                            <p className="text-muted mb-3">
                              This day has not been purchased yet. It's available for registration!
                            </p>
                            
                            {selectedDay.isFutureOrToday ? (
                              <div className="text-muted small mt-3">
                                Historical facts will be available once this day has passed.
                              </div>
                            ) : loadingFacts ? (
                              <div className="text-center py-3">
                                <Spinner animation="border" size="sm" className="me-2" />
                                <span className="text-muted">Loading interesting facts about this date...</span>
                              </div>
                            ) : dateFacts ? (
                              <div className="text-start mt-3">
                                {dateFacts.title && dateFacts.title.trim() && (
                                  <h5 className="mb-3 text-primary fw-bold">{dateFacts.title}</h5>
                                )}
                                {dateFacts.description && dateFacts.description.trim() && (
                                  <div 
                                    className="text-muted"
                                    style={{ 
                                      whiteSpace: "pre-wrap",
                                      lineHeight: "1.6",
                                      fontSize: "0.95rem"
                                    }}
                                  >
                                    {dateFacts.description}
                                  </div>
                                )}
                                {(!dateFacts.title || !dateFacts.title.trim()) && (!dateFacts.description || !dateFacts.description.trim()) && (
                                  <p className="text-muted">Loading facts...</p>
                                )}
                              </div>
                            ) : (
                              <div className="text-muted small">
                                Unable to load facts about this date.
                              </div>
                            )}
                            
                            <Button
                              variant="primary"
                              onClick={() => navigate(`/buy?date=${selectedDay.date}`)}
                              className="mt-3"
                            >
                              Purchase This Day
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          {selectedDay.title && (
                            <h4 className="brutal-subheading mb-3" style={{ color: "#0000FF" }}>{selectedDay.title}</h4>
                          )}

                          {selectedDay.image && (
                            <div className="mb-3" style={{ border: "4px solid #000000", overflow: "hidden" }}>
                              <Image
                                src={selectedDay.image}
                                alt={selectedDay.title || selectedDay.date}
                                fluid
                                style={{ maxHeight: "300px", objectFit: "cover", width: "100%", display: "block" }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            </div>
                          )}

                          {selectedDay.description && (
                            <p className="brutal-text mb-3" style={{ textTransform: "none", fontWeight: "400" }}>{selectedDay.description}</p>
                          )}

                          {/* Display personalized description if it exists */}
                          {selectedDay.personalizedDescription && selectedDay.personalizedDescription.trim() && (
                            <div className="mb-3" style={{ border: "3px solid #FFFF00", padding: "12px", background: "#FFFF00", color: "#000000" }}>
                              <div className="brutal-subheading mb-2" style={{ fontSize: "14px", fontWeight: "900" }}>PERSONAL NOTE:</div>
                              <p className="brutal-text mb-0" style={{ textTransform: "none", fontWeight: "400", fontSize: "14px", color: "#000000" }}>
                                {selectedDay.personalizedDescription}
                              </p>
                            </div>
                          )}

                          <div className="mb-3">
                            <Badge className="brutal-badge brutal-badge-primary">
                              OWNER: {selectedDay?.owner?.displayName || "UNKNOWN"}
                            </Badge>
                          </div>

                          {selectedDay?.owner && (selectedDay.owner._id === currentUserId || selectedDay.owner.toString() === currentUserId) && (
                            <Button
                              className="brutal-btn brutal-btn-success w-100"
                              onClick={() => generateCertificate(selectedDay.date)}
                            >
                              GENERATE PDF CERTIFICATE
                            </Button>
                          )}
                        </>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              )}
            </Row>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
